import React from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { FORM_FIELDS } from "@features/vessel-call/create/constants";
import type { VesselCallOutletContext } from "@features/vessel-call/create/CreateVesselCallContainer";
import { useDocumentUpload } from "@features/vessel-call/create/hooks/useDocumentUpload";
import type { VesselCallFormData } from "@features/vessel-call/create/schema/vesselCallFormSchema";
import { SasTokenPrefix } from "@services/user/types";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { useVesselCallFormStore } from "../../store/useVesselCallForm.store";

const LANG_BASE = "vessel-call.create";
const TEST_ID_BASE = "last-port-clearance";

const LastPortClearance: React.FC = () => {
  const { formData, updateFormData } = useOutletContext<VesselCallOutletContext>();
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const documentConditions = {
    ...FILE_UPLOAD_PROFILES.certificates,
    hideErrors: false,
  };

  const formStore = useVesselCallFormStore;

  const lastPortClearanceDocumentUrl = useDocumentUpload(
    FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL,
    SasTokenPrefix.VESSEL_CALL,
    formData,
    formStore,
  );

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${LANG_BASE}.form-sections.last-port-clearance`)}>
        <div className="col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL>
            name={FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL}
            type="file"
            uploadConditions={documentConditions}
            testId={`${TEST_ID_BASE}-file`}
            files={lastPortClearanceDocumentUrl.uploadEntries}
            setFiles={(files) => {
              lastPortClearanceDocumentUrl.setUploadEntries(files ?? []);
            }}
            onUpload={(_, files) => lastPortClearanceDocumentUrl.handleUpload(files)}
            onFieldChange={updateFormData}
            notSuccess={lastPortClearanceDocumentUrl.notSuccess}
          />
          {lastPortClearanceDocumentUrl.uploadEntries.length > 0 && (
            <div className="flex-1">
              <SingleFile
                testId={`${TEST_ID_BASE}-document-last-port-clearance`}
                file={lastPortClearanceDocumentUrl.uploadEntries[0]}
                fieldName={FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL}
                isLoading={lastPortClearanceDocumentUrl.isPending}
                setFiles={() => lastPortClearanceDocumentUrl.setUploadEntries([])}
                removeFn={() => {
                  lastPortClearanceDocumentUrl.handleRemove();
                }}
              />
            </div>
          )}
        </div>
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.LAST_PORT_CLEARANCE_ISSUE_DATE>
          name={FORM_FIELDS.LAST_PORT_CLEARANCE_ISSUE_DATE}
          type="date-picker"
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.LAST_PORT_CLEARANCE_ISSUE_DATE}`)}
          testId={`${TEST_ID_BASE}-issue-date`}
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="not-future"
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.LAST_PORT_CLEARANCE_CLASS_NAME>
          name={FORM_FIELDS.LAST_PORT_CLEARANCE_CLASS_NAME}
          type="text"
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.LAST_PORT_CLEARANCE_CLASS_NAME}`)}
          testId={`${TEST_ID_BASE}-class-name`}
          onFieldChange={updateFormData}
          maxChars={30}
        />
      </FormSection>
    </FormProvider>
  );
};
export default LastPortClearance;
